import { subscriptionApi } from '../apis/subscription.api';
import { deleteSession } from '../apis/user.api';
import { setSubscriptionIssueType, setSubscriptionInfo, unloadUnityApp } from '../redux/app.slice';
import { setEmail, setIdToken, setLoginStatus, resetUser, setOrganization } from '../redux/user.slice';

/*
 * The subscription gate the Login page applies, for the two ways a session is
 * restored WITHOUT the Login page: App's reload-from-cookie and the token
 * manager's reload-from-Firebase. Without this, a browser that stays signed in
 * outlives its organisation's subscription.
 *
 * Same rule as Login.js loadUserApp: the organisation's subscription expired
 * (the server already applies the endDate rule and the internal exemption), or
 * this user's own seat not active -> /subscribe, which shows a leader how to
 * renew and everyone else whom to ask. Anything less clear (no subscription in
 * the registry, the service unreachable) lets the session continue, as before.
 */

/** What the app keeps about the org's subscription from a validate-user answer. */
export const subscriptionInfoFrom = (status) =>
  status && status.success && status.hasSubscription && status.organization
    ? {
        orgName: status.organization.name || null,
        orgType: status.organization.orgType || null,
        planType: status.organization.planType || null,
        endDate: status.organization.endDate || null,
      }
    : null;

/**
 * 'explorer' | 'enterprise' | 'trial' — which way this org renews.
 * orgType from the login check first; the subscription's plan and the trial
 * tool's naming (<slug>-trial-<12>) when it is missing.
 */
export const renewalKind = ({ orgType, planType, orgName } = {}) => {
  if (orgType === 'trial' || /-trial-[a-z0-9]{12}$/.test(orgName || '')) return 'trial';
  if (orgType === 'explorer' || (!orgType && planType === 'explorer')) return 'explorer';
  return 'enterprise';
};

/** 'org' | 'user' | null — why this login should go to /subscribe, if it should. */
export const subscriptionIssue = (status) => {
  if (!status || !status.success || !status.hasSubscription) return null;
  if (status.organization?.subscriptionStatus === 'expired') return 'org';
  if (status.user?.subscriptionStatus !== 'active') return 'user';
  return null;
};

/**
 * Check, and if needed redirect to /subscribe with the state that page needs
 * (email, token, organisation, why). Returns true when it redirected.
 *
 * Deliberately does NOT sign out of Firebase: the token manager listens for
 * sign-out and would send the user to /login, wiping the state /subscribe
 * reads. The app session is ended instead, and the next sign-in passes through
 * Login's own gate.
 */
export const redirectIfSubscriptionBlocked = async ({ dispatch, navigate, accessToken, email }) => {
  if (!accessToken || !email) return false;
  let status;
  try {
    status = await subscriptionApi.validateUserSubscriptionByEmail(accessToken, email);
  } catch (err) {
    return false;
  }
  await dispatch(setSubscriptionInfo(subscriptionInfoFrom(status)));
  const issue = subscriptionIssue(status);
  if (!issue) return false;

  console.log(`---- subscription gate on reload: ${issue} issue, redirecting to /subscribe`);
  await dispatch(setSubscriptionIssueType(issue));
  await dispatch(unloadUnityApp(true));
  await dispatch(setLoginStatus(false));
  await dispatch(resetUser());
  await deleteSession();
  await dispatch(setEmail(email));
  await dispatch(setIdToken(accessToken));
  await dispatch(setOrganization(status.organization.name));
  navigate('/subscribe');
  return true;
};
